import {
    ApiContentSection,
    ApiFullLandingPageLink,
    ApiSectionType,
    HIGHLIGHTED_PRODUCT_HASH,
    toApiFullLandingPageLink,
} from '@storefronts/common-types'
import { useContext, useEffect, useState } from 'react'
import { ContentBlockContext } from '../../../context/contentBlockContext'
import { LandingPageLinksContext } from '../../../context/LandingPageLinksContext'
import { ProductContext } from '../../../context/productContext'
import { Reviews } from '../reviews/Reviews'
import { SectionContainer } from './Shared'
import { useCombinedProductHistoryAndLandingPageLinks } from '../../../utils/useCombinedProductHistory'

type ReviewSectionProps = Extract<
    ApiContentSection,
    { type: ApiSectionType.REVIEWS_SECTION }
>

interface ReviewsSectionProps {
    section: ReviewSectionProps
    /**
     * When true, renders without the outer SectionContainer so the caller can
     * control horizontal padding (used inside inline PDP tabs).
     */
    compact?: boolean
    /** Already-resolved product (e.g. product drawer). Hash lookup is used otherwise. */
    productLink?: ApiFullLandingPageLink
}

export const ReviewsSection: React.FC<ReviewsSectionProps> = ({
    section,
    compact,
    productLink,
}) => {
    const { contentReviewsRef } = useContext(ContentBlockContext)
    const { reviewsRef } = useContext(ProductContext)
    const { fetchAndCacheLinksForHashes } = useContext(LandingPageLinksContext)
    const combinedLinksWithReviews =
        useCombinedProductHistoryAndLandingPageLinks().filter(
            (link) => link?.reviews?.count && link?.reviews?.count > 0,
        )

    const highlightsMainProductSlot =
        section.linkHash === HIGHLIGHTED_PRODUCT_HASH

    /** Resolved synchronously when possible so the tree can SSR instead of hydrating from null via useEffect. */
    const lpLinkFromLandingData: ApiFullLandingPageLink | null =
        productLink ??
        toApiFullLandingPageLink(
            highlightsMainProductSlot
                ? (combinedLinksWithReviews[0] ?? null)
                : (combinedLinksWithReviews.find(
                      (l) => l.linkHash === section.linkHash,
                  ) ?? null),
        )

    const [
        landingPageLinkFromFetchedBatch,
        setLandingPageLinkFromFetchedBatch,
    ] = useState<ApiFullLandingPageLink | null>(null)

    useEffect(() => {
        if (lpLinkFromLandingData) return
        if (highlightsMainProductSlot) return

        let isCancelled = false
        fetchAndCacheLinksForHashes([section.linkHash])
            .then((links) => {
                if (!isCancelled)
                    setLandingPageLinkFromFetchedBatch(
                        toApiFullLandingPageLink(links[0] ?? null),
                    )
            })
            .catch(() => {
                if (!isCancelled) setLandingPageLinkFromFetchedBatch(null)
            })
        return () => {
            isCancelled = true
        }
    }, [
        highlightsMainProductSlot,
        lpLinkFromLandingData,
        section.linkHash,
        fetchAndCacheLinksForHashes,
    ])

    const lpLink = lpLinkFromLandingData ?? landingPageLinkFromFetchedBatch
    if (!lpLink) return null

    const variantShopifyProductId = section.variantShopifyProductId
    const reviewsKey = `${lpLink.linkHash}-${variantShopifyProductId ?? ''}`

    if (compact) {
        return (
            <div
                style={{
                    // Give inline PDP a clear breathing space between description
                    // / key features and the first review block.
                    marginTop: 24,
                }}
            >
                <Reviews
                    key={reviewsKey}
                    location="content-block"
                    ref={contentReviewsRef || reviewsRef}
                    lpLink={lpLink}
                    variantShopifyProductId={variantShopifyProductId}
                />
            </div>
        )
    }

    return (
        <SectionContainer>
            <Reviews
                key={reviewsKey}
                location="content-block"
                ref={contentReviewsRef || reviewsRef}
                lpLink={lpLink}
                variantShopifyProductId={variantShopifyProductId}
            />
        </SectionContainer>
    )
}
